import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Copy, Info, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { sameValue, useDirtyForm } from '../../../hooks/useDirtyForm';
import { categoryToneOf } from '../../../utils/catalogHelpers';
import { useToast } from '../../shared/Toast';
import { CatalogToneDot } from '../../shared/CatalogColorPicker';
import {
  LibraryCategory, OptionChoiceBody, OptionGroup, OptionGroupPatch, SelectionType,
  createOptionChoice, createOptionGroup, deleteOptionChoice, deleteOptionGroup, getCategoryDefaultGroupIds,
  listCategories, listOptionGroups, setCategoryDefaultGroupIds, updateOptionChoice, updateOptionGroup,
} from './api';
import {
  BADGE, BADGE_OUTLINE, BTN, BTN_PRIMARY, COLS_HEAD, CardSaveBar, EmptyState, FIELD, HINT, INVALID, CHANGED, LABEL,
  LibraryPane, LoadState, OPEN_ROW, RowMenu, SearchField, Segmented, Toggle, UsageCount, searchKey,
  similarName, useCatalogLock, useLibraryErrorText,
} from './parts';
import { RowDraft, RowsEditor, RowsReadOnly, newRowKey, rowKeyOf, validateRows } from './RowsEditor';
import { useLibraryDraftGuard } from './draftGuard';

/**
 * Κατάλογος → «Ομάδες επιλογών» (mock Φάσης 3, 12/10/2026 — κατάσταση c). Οι ομάδες είναι ΚΟΙΝΕΣ και
 * ζωντανές: μια αλλαγή εδώ ισχύει σε κάθε προϊόν που έχει την ομάδα. Απόφαση ιδιοκτήτη: η κοινή ομάδα
 * αλλάζει ΜΟΝΟ εδώ — το πάνελ προϊόντος μόνο συνδέει/αποσυνδέει (και φτιάχνει νέα μέσω του api.ts).
 *
 * Μία ανοιχτή ομάδα τη φορά, πρόχειρο μέχρι την «Αποθήκευση» της κάρτας (hooks/useDirtyForm, με
 * guardId: η έξοδος από τη σελίδα ρωτά). «Μπαίνει αυτόματα στα νέα προϊόντα των κατηγοριών» = ο
 * πίνακας product_category_default_option_groups, που ο server κρατά ανά κατηγορία.
 */

interface GroupDraft {
  name: string;
  selection_type: SelectionType;
  is_required: boolean;
  choices: RowDraft[];
  /** Με τη σειρά του δέντρου κατηγοριών (για σταθερή σύγκριση). */
  categoryIds: number[];
}

const EMPTY_DRAFT: GroupDraft = { name: '', selection_type: 'single', is_required: false, choices: [], categoryIds: [] };

const blankRow = (): RowDraft => ({ key: newRowKey(), id: null, name: '', price: 0, is_default: false });

const draftOf = (g: OptionGroup, categoryIds: number[]): GroupDraft => ({
  name: g.name,
  selection_type: g.selection_type,
  is_required: g.is_required,
  choices: g.choices.map(c => ({ key: rowKeyOf(c.id), id: c.id, name: c.name, price: c.price_modifier, is_default: c.is_default })),
  categoryIds,
});

/** Νέα γραμμή που έμεινε όπως μπήκε (χωρίς όνομα, 0 €) δεν είναι επιλογή — απλώς δεν στέλνεται. */
const keptRows = (rows: RowDraft[]) => rows.filter(r => r.id != null || r.name.trim() || r.price !== 0);

/** «Μία»: μένει μόνο η πρώτη προεπιλογή. */
const keepFirstDefault = (rows: RowDraft[]): RowDraft[] => {
  let seen = false;
  return rows.map(r => {
    if (!r.is_default) return r;
    if (seen) return { ...r, is_default: false };
    seen = true;
    return r;
  });
};

interface CategoryOption { cat: LibraryCategory; label: string; tone: string }

/** Γονείς με τη σειρά τους και κάτω από τον καθένα τα παιδιά του («Καφέδες › Ζεστοί»). */
function categoryTree(list: LibraryCategory[]): CategoryOption[] {
  const parents = list.filter(c => c.parent_id == null);
  const out: CategoryOption[] = [];
  parents.forEach((p, i) => {
    out.push({ cat: p, label: p.name, tone: categoryToneOf(p, i) });
    list.filter(c => c.parent_id === p.id).forEach((c, j) => {
      out.push({ cat: c, label: `${p.name} › ${c.name}`, tone: categoryToneOf(c, j) });
    });
  });
  const placed = new Set(out.map(o => o.cat.id));
  list.filter(c => !placed.has(c.id)).forEach((c, i) => {
    out.push({ cat: c, label: c.parent_name ? `${c.parent_name} › ${c.name}` : c.name, tone: categoryToneOf(c, i) });
  });
  return out;
}

type Open = number | 'new' | null;
interface Categories { state: 'loading' | 'ready' | 'error'; list: LibraryCategory[]; defaults: Record<number, number[]> }

const OptionGroupsTab: React.FC = () => {
  const { t } = useTranslation(['products', 'common', 'settings']);
  const { store } = useAuth();
  const storeId = store?.id;
  const { showToast } = useToast();
  const errorText = useLibraryErrorText();
  const { locked, noteError } = useCatalogLock();
  const form = useDirtyForm<GroupDraft>(EMPTY_DRAFT, { guardId: 'catalog:option-groups' });
  useLibraryDraftGuard('option-groups', form.dirty);
  const { values: v, saved } = form;

  const [groups, setGroups] = useState<OptionGroup[]>([]);
  const [load, setLoad] = useState<'loading' | 'ready' | 'error'>('loading');
  const [cats, setCats] = useState<Categories>({ state: 'loading', list: [], defaults: {} });
  const [open, setOpen] = useState<Open>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const tree = useMemo(() => categoryTree(cats.list), [cats.list]);
  const treeOrder = useMemo(() => new Map(tree.map((o, i) => [o.cat.id, i])), [tree]);
  const sortCats = useCallback((ids: number[]) => ids.slice().sort((a, b) => (treeOrder.get(a) ?? 1e9) - (treeOrder.get(b) ?? 1e9)), [treeOrder]);
  const categoryIdsOf = useCallback((groupId: number, defaults: Record<number, number[]>) =>
    tree.filter(o => (defaults[o.cat.id] || []).includes(groupId)).map(o => o.cat.id), [tree]);

  const loadGroups = useCallback(async (): Promise<OptionGroup[] | null> => {
    if (!storeId) return null;
    try {
      const list = await listOptionGroups(storeId);
      if (!alive.current) return null;
      setGroups(list);
      setLoad('ready');
      return list;
    } catch (err) {
      console.error('Option groups load failed:', err);
      if (alive.current) setLoad(prev => (prev === 'ready' ? prev : 'error'));
      return null;
    }
  }, [storeId]);

  const loadCategories = useCallback(async () => {
    if (!storeId) return;
    setCats(c => ({ ...c, state: 'loading' }));
    try {
      const list = await listCategories(storeId);
      const ids = await Promise.all(list.map(c => getCategoryDefaultGroupIds(c.id)));
      const defaults: Record<number, number[]> = {};
      list.forEach((c, i) => { defaults[c.id] = ids[i]; });
      if (alive.current) setCats({ state: 'ready', list, defaults });
    } catch (err) {
      console.error('Category defaults load failed:', err);
      if (alive.current) setCats(c => ({ ...c, state: 'error' }));
    }
  }, [storeId]);

  useEffect(() => {
    setOpen(null);
    form.reset(EMPTY_DRAFT);
    setLoad('loading');
    loadGroups();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- μόνο σε αλλαγή καταστήματος
  }, [storeId]);

  // Κλείδωσε (κεντρικός κατάλογος, π.χ. 403 από τον server): το πρόχειρο δεν θα αποθηκευτεί ποτέ.
  useEffect(() => {
    if (!locked) return;
    if (open === 'new') { setOpen(null); form.reset(EMPTY_DRAFT); } else if (form.dirty) form.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, form.dirty, open]);

  // Οι κατηγορίες ήρθαν μετά το άνοιγμα: μπαίνουν στη βάση σύγκρισης (το πεδίο ήταν κλειδωμένο ως τότε).
  useEffect(() => {
    if (cats.state !== 'ready' || typeof open !== 'number') return;
    const ids = categoryIdsOf(open, cats.defaults);
    if (sameValue(form.saved.categoryIds, ids)) return;
    const nextValues = { ...form.values, categoryIds: ids };
    form.reset({ ...form.saved, categoryIds: ids });
    form.setValues(() => nextValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats]);

  const openGroup = groups.find(g => g.id === open) || null;

  /** Με αλλαγές στην ανοιχτή κάρτα ρωτά αν χάνονται. false = ο χρήστης έμεινε. */
  const confirmDropDraft = (): boolean => {
    if (!form.dirty) return true;
    if (open === 'new') return window.confirm(t('products:libraries.discardNew'));
    const name = (openGroup?.name || v.name).trim() || t('products:libraries.unnamed');
    return window.confirm(t('products:libraries.discard', { name }));
  };

  /** Αλλάζει ανοιχτή κάρτα (null = καμία). */
  const switchTo = (next: Open): boolean => {
    if (!confirmDropDraft()) return false;
    setCardError(null);
    setShowErrors(false);
    setOpen(next);
    if (next === null) form.reset(EMPTY_DRAFT);
    else if (next === 'new') form.reset({ ...EMPTY_DRAFT, choices: [blankRow(), blankRow()] });
    else {
      const g = groups.find(x => x.id === next);
      if (g) form.reset(draftOf(g, cats.state === 'ready' ? categoryIdsOf(g.id, cats.defaults) : []));
    }
    return true;
  };

  /* ───────────── έλεγχος πριν την αποθήκευση ───────────── */

  const rows = keptRows(v.choices);
  const savedRows = useMemo(() => new Map(saved.choices.map(r => [r.key, r])), [saved.choices]);
  const rowErrors = validateRows(rows, savedRows, {
    allowNegative: true,
    messages: {
      nameRequired: t('products:libraries.validation.rowNameRequired'),
      duplicate: name => t('products:libraries.validation.rowNameDuplicate', { name }),
      priceInvalid: t('products:libraries.validation.priceInvalid'),
      priceNegative: t('products:libraries.validation.priceNegative'),
    },
  });
  const nameError = (() => {
    const name = v.name.trim();
    if (!name) return t('products:libraries.validation.nameRequired');
    const renamed = open === 'new' || name !== saved.name.trim();
    if (renamed && groups.some(g => g.id !== open && similarName(g.name, name))) return t('products:libraries.validation.nameTaken', { name });
    return null;
  })();
  const valid = !nameError && Object.keys(rowErrors).length === 0;

  /* ───────────── αποθήκευση ───────────── */

  /** Μετά από εγγραφές: η λίστα και οι κατηγορίες από τον server, το πρόχειρο πάνω στα νέα δεδομένα. */
  const settle = async (groupId: number, draft: GroupDraft, touched: Set<number>): Promise<boolean> => {
    const fresh = await loadGroups();
    let defaults = cats.defaults;
    if (touched.size) {
      try {
        const pairs = await Promise.all(Array.from(touched).map(async cid => [cid, await getCategoryDefaultGroupIds(cid)] as const));
        defaults = { ...defaults };
        pairs.forEach(([cid, ids]) => { defaults[cid] = ids; });
        if (alive.current) setCats(c => ({ ...c, defaults }));
      } catch (err) {
        console.error('Category defaults refresh failed:', err);
      }
    }
    if (!alive.current) return false;
    const g = fresh?.find(x => x.id === groupId);
    if (!fresh || !g) {
      setOpen(null);
      form.reset(EMPTY_DRAFT);
      if (!fresh) setLoad('error');
      return false;
    }
    const nextSaved = draftOf(g, categoryIdsOf(g.id, defaults));
    // Ό,τι δεν πέρασε μένει στο πρόχειρο· οι νέες γραμμές που πέρασαν παίρνουν το id τους (όνομα = μοναδικό).
    const claimed = new Set(keptRows(draft.choices).filter(r => r.id != null).map(r => r.id));
    const choices = keptRows(draft.choices).map(r => {
      const name = r.name.trim();
      if (r.id != null) return { ...r, name };
      const match = g.choices.find(c => !claimed.has(c.id) && c.name === name);
      if (!match) return { ...r, name };
      claimed.add(match.id);
      return { ...r, key: rowKeyOf(match.id), id: match.id, name };
    });
    const nextValues: GroupDraft = { ...draft, name: draft.name.trim(), choices, categoryIds: sortCats(draft.categoryIds) };
    setOpen(g.id);
    form.reset(nextSaved);
    if (!sameValue(nextSaved, nextValues)) form.setValues(() => nextValues);
    return true;
  };

  const syncCategories = async (groupId: number, add: number[], remove: number[], touched: Set<number>) => {
    for (const cid of add) {
      touched.add(cid);
      const ids = await getCategoryDefaultGroupIds(cid);
      if (!ids.includes(groupId)) await setCategoryDefaultGroupIds(cid, [...ids, groupId]);
    }
    for (const cid of remove) {
      touched.add(cid);
      const ids = await getCategoryDefaultGroupIds(cid);
      if (ids.includes(groupId)) await setCategoryDefaultGroupIds(cid, ids.filter(id => id !== groupId));
    }
  };

  const saveExisting = async (g: OptionGroup) => {
    const draft = form.values;
    const kept = keptRows(draft.choices);
    const touched = new Set<number>();
    let failure: unknown = null;
    try {
      const patch: OptionGroupPatch = {};
      if (draft.name.trim() !== g.name) patch.name = draft.name.trim();
      if (draft.selection_type !== g.selection_type) patch.selection_type = draft.selection_type;
      if (draft.is_required !== g.is_required) patch.is_required = draft.is_required;
      // Πρώτα η ομάδα: η «Μία» του server σβήνει τις άλλες προεπιλογές με βάση τον τύπο της.
      if (Object.keys(patch).length) await updateOptionGroup(g.id, patch);

      for (const c of g.choices) {
        if (!kept.some(r => r.id === c.id)) await deleteOptionChoice(c.id);
      }
      const savedOrder = g.choices.filter(c => kept.some(r => r.id === c.id)).map(c => c.id).join();
      const draftOrder = kept.filter(r => r.id != null).map(r => r.id).join();
      const renumber = kept.some(r => r.id == null) || savedOrder !== draftOrder;
      for (let index = 0; index < kept.length; index++) {
        const r = kept[index];
        const c = r.id == null ? null : g.choices.find(x => x.id === r.id);
        if (!c) continue;
        const p: Partial<OptionChoiceBody> = {};
        if (r.name.trim() !== c.name) p.name = r.name.trim();
        if (r.price !== c.price_modifier) p.price_modifier = r.price as number;
        if (r.is_default !== c.is_default) p.is_default = r.is_default;
        if (renumber && c.display_order !== index) p.display_order = index;
        if (Object.keys(p).length) await updateOptionChoice(c.id, p);
      }
      for (let index = 0; index < kept.length; index++) {
        const r = kept[index];
        if (r.id != null) continue;
        await createOptionChoice(g.id, { name: r.name.trim(), price_modifier: r.price as number, is_default: r.is_default, display_order: index });
      }
      const before = form.saved.categoryIds;
      await syncCategories(
        g.id,
        draft.categoryIds.filter(id => !before.includes(id)),
        before.filter(id => !draft.categoryIds.includes(id)),
        touched,
      );
    } catch (err) {
      failure = err;
      noteError(err);
    }
    const ok = await settle(g.id, draft, touched);
    if (!alive.current) return;
    if (failure) setCardError(t('products:libraries.errors.partial', { reason: errorText(failure) }));
    else if (ok) showToast('success', t('products:libraries.savedToast', { name: draft.name.trim() }));
    else setListError(t('products:libraries.errors.notFound'));
  };

  const saveNew = async () => {
    if (!storeId) return;
    const draft = form.values;
    let created: OptionGroup;
    try {
      created = await createOptionGroup(storeId, {
        name: draft.name.trim(),
        selection_type: draft.selection_type,
        is_required: draft.is_required,
        choices: keptRows(draft.choices).map(r => ({ name: r.name.trim(), price_modifier: r.price as number, is_default: r.is_default })),
      });
    } catch (err) {
      noteError(err);
      setCardError(errorText(err));
      return;
    }
    const touched = new Set<number>();
    let failure: unknown = null;
    try {
      await syncCategories(created.id, draft.categoryIds, [], touched);
    } catch (err) {
      failure = err;
      noteError(err);
    }
    // Η νέα ομάδα ανοίγει ως υπάρχουσα· ό,τι από τις κατηγορίες δεν πέρασε μένει αλλαγή.
    const asExisting: GroupDraft = { ...draftOf(created, []), categoryIds: draft.categoryIds };
    await settle(created.id, asExisting, touched);
    if (!alive.current) return;
    if (failure) setCardError(t('products:libraries.errors.partial', { reason: errorText(failure) }));
    else showToast('success', t('products:libraries.createdToast', { name: created.name }));
  };

  const save = async () => {
    if (saving || locked) return;
    if (!valid) { setShowErrors(true); return; }
    setSaving(true);
    setCardError(null);
    try {
      if (open === 'new') await saveNew();
      else if (openGroup) await saveExisting(openGroup);
    } finally {
      if (alive.current) { setSaving(false); setShowErrors(false); }
    }
  };

  const reset = () => {
    setCardError(null);
    setShowErrors(false);
    if (open === 'new') {
      if (form.dirty && !window.confirm(t('products:libraries.discardNew'))) return;
      setOpen(null);
      form.reset(EMPTY_DRAFT);
      return;
    }
    form.reset();
  };

  /* ───────────── αντίγραφο / διαγραφή ───────────── */

  const copyName = (name: string) => {
    const taken = (n: string) => groups.some(g => similarName(g.name, n));
    let candidate = t('products:libraries.copyName', { name });
    for (let i = 2; taken(candidate) && i < 100; i++) candidate = t('products:libraries.copyNameN', { name, n: i });
    return candidate.slice(0, 100);
  };

  const duplicate = async (g: OptionGroup) => {
    if (!storeId || !confirmDropDraft()) return;
    setListError(null);
    setCardError(null);
    try {
      const created = await createOptionGroup(storeId, {
        name: copyName(g.name),
        selection_type: g.selection_type,
        is_required: g.is_required,
        choices: g.choices.map(c => ({ name: c.name, price_modifier: c.price_modifier, is_default: c.is_default })),
      });
      await settle(created.id, draftOf(created, []), new Set());
      if (alive.current) showToast('success', t('products:libraries.createdToast', { name: created.name }));
    } catch (err) {
      noteError(err);
      setListError(t('products:libraries.errors.actionFailed', { reason: errorText(err) }));
    }
  };

  const remove = async (g: OptionGroup) => {
    const message = g.product_count
      ? t('products:libraries.options.deleteConfirmUsed', { name: g.name, count: g.product_count })
      : t('products:libraries.options.deleteConfirm', { name: g.name });
    if (!window.confirm(message)) return;
    setListError(null);
    try {
      await deleteOptionGroup(g.id);
    } catch (err) {
      noteError(err);
      setListError(t('products:libraries.errors.actionFailed', { reason: errorText(err) }));
      return;
    }
    if (open === g.id) { setOpen(null); form.reset(EMPTY_DRAFT); }
    // Ο server σβήνει και τις «αυτόματα σε κατηγορίες» (CASCADE).
    setCats(c => ({
      ...c,
      defaults: Object.fromEntries(Object.entries(c.defaults).map(([cid, ids]) => [cid, ids.filter(id => id !== g.id)])),
    }));
    await loadGroups();
    if (alive.current) showToast('success', t('products:libraries.deletedToast', { name: g.name }));
  };

  /* ───────────── εμφάνιση ───────────── */

  const visible = useMemo(() => {
    const q = searchKey(search.trim());
    if (!q) return groups;
    return groups.filter(g => g.id === open || searchKey(g.name).includes(q) || g.choices.some(c => searchKey(c.name).includes(q)));
  }, [groups, search, open]);

  const rowLabels = {
    nameCol: t('products:libraries.options.choiceCol'),
    priceCol: t('products:libraries.options.extraCol'),
    defaultCol: t('products:libraries.options.defaultCol'),
    add: t('products:libraries.options.addChoice'),
    namePlaceholder: t('products:libraries.options.choicePlaceholder'),
    nameAria: (n: number) => t('products:libraries.options.choiceName', { n }),
    priceAria: (name: string) => t('products:libraries.options.choicePrice', { name }),
    defaultAria: (name: string) => t('products:libraries.options.choiceDefault', { name }),
    removeAria: (name: string) => t('products:libraries.removeRow', { name }),
    moveAria: (name: string) => t('products:libraries.moveRow', { name }),
  };

  const GRID = 'grid grid-cols-[16px_minmax(0,1fr)_72px] md:grid-cols-[16px_minmax(170px,330px)_minmax(0,1fr)_96px] items-center gap-x-2.5';

  const typeLabel = (type: SelectionType) => (type === 'single' ? t('products:libraries.options.single') : t('products:libraries.options.multiple'));

  const preview = (g: OptionGroup) => {
    if (!g.choices.length) return <span className="text-t4">{t('products:libraries.options.noChoices')}</span>;
    const shown = g.choices.slice(0, 6);
    return (
      <>
        {shown.map((c, i) => (
          <React.Fragment key={c.id}>
            {i > 0 && ' · '}
            {c.is_default ? <b className="font-semibold text-t1">{c.name}</b> : c.name}
          </React.Fragment>
        ))}
        {g.choices.length > 6 && ` · +${g.choices.length - 6}`}
      </>
    );
  };

  const body = (g: OptionGroup | null) => {
    const isNew = g === null;
    if (locked && g) return <ReadOnlyBody group={g} tree={tree} cats={cats} categoryIdsOf={categoryIdsOf} typeLabel={typeLabel} />;
    return (
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 pb-4 pl-[18px] pr-[18px] pt-0.5 md:pl-[46px] xl:grid-cols-[minmax(0,1.1fr)_minmax(300px,1fr)]">
        <RowsEditor
          rows={v.choices}
          saved={isNew ? new Map(v.choices.map(r => [r.key, r])) : savedRows}
          onChange={choices => form.setField('choices', choices)}
          exclusiveDefault={v.selection_type === 'single'}
          labels={rowLabels}
          errors={rowErrors}
          showErrors={showErrors}
          hint={t('products:libraries.options.zeroHint')}
        />
        <GroupSettings
          isNew={isNew}
          draft={v}
          saved={saved}
          nameError={showErrors ? nameError : null}
          onName={name => form.setField('name', name)}
          onType={type => form.setValues(prev => ({ ...prev, selection_type: type, choices: type === 'single' ? keepFirstDefault(prev.choices) : prev.choices }))}
          onRequired={req => form.setField('is_required', req)}
          onCategories={ids => form.setField('categoryIds', sortCats(ids))}
          tree={tree}
          cats={cats}
          onRetryCategories={loadCategories}
          usage={g ? g.product_count : null}
          typeLabel={typeLabel}
        />
        <div className="xl:col-span-2">
          <CardSaveBar
            dirty={form.dirty}
            saving={saving}
            canSave={!showErrors || valid}
            onSave={save}
            onReset={reset}
            isNew={isNew}
            error={cardError}
          />
        </div>
      </div>
    );
  };

  return (
    <LibraryPane
      testId="option-groups-tab"
      title={t('products:libraries.options.title')}
      count={load === 'ready' ? groups.length : null}
      hint={t('products:libraries.options.hint')}
      locked={locked}
      actions={(
        <>
          <SearchField value={search} onChange={setSearch} placeholder={t('products:libraries.options.search')} />
          {!locked && (
            <button type="button" onClick={() => switchTo('new')} disabled={load !== 'ready' || open === 'new'} className={BTN_PRIMARY}>
              <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
              {t('products:libraries.options.new')}
            </button>
          )}
        </>
      )}
    >
      {listError && (
        <p role="alert" className="mx-[18px] mt-3 rounded-[10px] border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-800 dark:text-red-200">
          {listError}
        </p>
      )}
      {load !== 'ready' ? (
        <LoadState state={load} onRetry={() => { setLoad('loading'); loadGroups(); }} />
      ) : (
        <>
          <div className={`${COLS_HEAD} mt-3 flex pr-3.5`}>
            <div className={`${GRID} flex-1 py-2 pl-[18px]`}>
              <span />
              <span>{t('products:libraries.options.colGroup')}</span>
              <span className="hidden md:block">{t('products:libraries.options.colChoices')}</span>
              <span className="text-right">{t('products:libraries.usedIn')}</span>
            </div>
            <span className="w-8 shrink-0" />
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            {open === 'new' && (
              <div data-testid="option-group-new" className={`border-b border-line ${OPEN_ROW}`}>
                <div className="flex min-h-[52px] flex-wrap items-center gap-x-2.5 gap-y-0.5 py-2 pl-[18px] pr-3.5">
                  <Plus className="h-4 w-4 text-brand" aria-hidden="true" />
                  <b className="text-sm font-semibold text-t1">{t('products:libraries.options.newTitle')}</b>
                  <span className={HINT}>{t('products:libraries.options.newHint')}</span>
                </div>
                {body(null)}
              </div>
            )}
            {groups.length === 0 && open !== 'new' ? (
              <EmptyState
                title={t('products:libraries.options.empty')}
                hint={t('products:libraries.options.emptyHint')}
                action={!locked && (
                  <button type="button" onClick={() => switchTo('new')} className={BTN}>
                    <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
                    {t('products:libraries.options.new')}
                  </button>
                )}
              />
            ) : visible.length === 0 && open !== 'new' ? (
              <EmptyState title={t('products:libraries.noMatches', { query: search.trim() })} />
            ) : (
              visible.map(g => {
                const isOpen = open === g.id;
                return (
                  <div key={g.id} data-testid={`option-group-${g.id}`} className={`border-b border-line ${isOpen ? OPEN_ROW : ''}`}>
                    <div className="flex items-center pr-3.5">
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => switchTo(isOpen ? null : g.id)}
                        className={`${GRID} min-h-[52px] flex-1 min-w-0 py-1.5 pl-[18px] text-left ${isOpen ? '' : 'hover:bg-surface-3/35'}`}
                      >
                        <span className="text-t3">
                          {isOpen ? <ChevronDown className="h-[15px] w-[15px]" aria-hidden="true" /> : <ChevronRight className="h-[15px] w-[15px]" aria-hidden="true" />}
                        </span>
                        <span className="flex min-w-0 flex-wrap items-center gap-x-[7px] gap-y-1">
                          <b className="truncate text-sm font-semibold text-t1">{g.name}</b>
                          {isOpen && form.dirty && (
                            <span
                              role="img"
                              aria-label={t('products:libraries.changedMark')}
                              title={t('products:libraries.changedMark')}
                              className="h-[7px] w-[7px] shrink-0 rounded-full bg-amber-500 shadow-[0_0_0_3px_rgb(245_158_11_/_22%)]"
                            />
                          )}
                          <span className={BADGE}>{typeLabel(g.selection_type)}</span>
                          {g.is_required && <span className={BADGE_OUTLINE}>{t('products:libraries.options.required')}</span>}
                        </span>
                        <span className="hidden truncate text-[12.5px] text-t3 md:block">{preview(g)}</span>
                        <span className="text-right text-[13px] font-semibold text-t2"><UsageCount count={g.product_count} /></span>
                      </button>
                      <div className="flex w-8 shrink-0 justify-end">
                        {!locked && (
                          <RowMenu
                            label={t('products:libraries.moreActions', { name: g.name })}
                            items={[
                              { label: t('products:libraries.duplicate'), icon: Copy, onSelect: () => { duplicate(g); } },
                              { label: t('common:delete'), icon: Trash2, danger: true, onSelect: () => { remove(g); } },
                            ]}
                          />
                        )}
                      </div>
                    </div>
                    {isOpen && body(g)}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </LibraryPane>
  );
};

/* ───────────────────────────── δεξιά στήλη της κάρτας ───────────────────────────── */

const GroupSettings: React.FC<{
  isNew: boolean;
  draft: GroupDraft;
  saved: GroupDraft;
  nameError: string | null;
  onName: (name: string) => void;
  onType: (type: SelectionType) => void;
  onRequired: (required: boolean) => void;
  onCategories: (ids: number[]) => void;
  tree: CategoryOption[];
  cats: Categories;
  onRetryCategories: () => void;
  usage: number | null;
  typeLabel: (type: SelectionType) => string;
}> = ({ isNew, draft, saved, nameError, onName, onType, onRequired, onCategories, tree, cats, onRetryCategories, usage, typeLabel }) => {
  const { t } = useTranslation(['products']);
  const nameId = useId();
  const requiredId = useId();
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <label htmlFor={nameId} className={LABEL}>{t('products:libraries.options.nameLabel')}</label>
        <input
          id={nameId}
          type="text"
          value={draft.name}
          maxLength={100}
          autoFocus={isNew}
          placeholder={t('products:libraries.options.namePlaceholder')}
          onChange={e => onName(e.target.value)}
          aria-invalid={nameError ? true : undefined}
          className={`${FIELD} ${!isNew && draft.name !== saved.name ? CHANGED : ''} ${nameError ? INVALID : ''}`}
        />
        {nameError && <p className="text-xs text-red-700 dark:text-red-300">{nameError}</p>}
      </div>
      <div className="flex flex-col gap-1.5">
        <span className={LABEL}>{t('products:libraries.options.picks')}</span>
        <div>
          <Segmented<SelectionType>
            value={draft.selection_type}
            onChange={onType}
            label={t('products:libraries.options.picks')}
            changed={!isNew && draft.selection_type !== saved.selection_type}
            options={[
              { value: 'single', label: typeLabel('single') },
              { value: 'multiple', label: typeLabel('multiple') },
            ]}
          />
        </div>
      </div>
      <div className="flex items-start gap-2.5">
        <span className="pt-px">
          <Toggle
            checked={draft.is_required}
            onChange={onRequired}
            labelledBy={requiredId}
            changed={!isNew && draft.is_required !== saved.is_required}
          />
        </span>
        <span className="min-w-0">
          <b id={requiredId} className="block text-[13.5px] font-semibold text-t1">{t('products:libraries.options.required')}</b>
          <span className={`block ${HINT}`}>{t('products:libraries.options.requiredHint')}</span>
        </span>
      </div>
      <CategoryAttach
        value={draft.categoryIds}
        saved={isNew ? [] : saved.categoryIds}
        onChange={onCategories}
        tree={tree}
        cats={cats}
        onRetry={onRetryCategories}
      />
      {!isNew && (
        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>
            {usage == null
              ? t('products:libraries.usageUnknownLabel')
              : usage === 0 ? t('products:libraries.usageNone') : t('products:libraries.usage', { count: usage })}
          </span>
          <p className="flex items-start gap-2 rounded-[10px] border border-line bg-surface-1 dark:bg-surface-0 px-3 py-2 text-[12.5px] text-t2">
            <Info className="mt-px h-[15px] w-[15px] shrink-0 text-t3" aria-hidden="true" />
            {t('products:libraries.options.linkHint')}
          </p>
        </div>
      )}
    </div>
  );
};

/** «Μπαίνει αυτόματα στα νέα προϊόντα των κατηγοριών»: τσιπ με × και «+ Κατηγορία». */
const CategoryAttach: React.FC<{
  value: number[];
  saved: number[];
  onChange?: (ids: number[]) => void;
  tree: CategoryOption[];
  cats: Categories;
  onRetry?: () => void;
  readOnly?: boolean;
}> = ({ value, saved, onChange, tree, cats, onRetry, readOnly = false }) => {
  const { t } = useTranslation(['products']);
  const labelId = useId();
  const byId = new Map(tree.map(o => [o.cat.id, o]));
  const available = tree.filter(o => !value.includes(o.cat.id));
  return (
    <div className="flex flex-col gap-1.5" role="group" aria-labelledby={labelId}>
      <span id={labelId} className={LABEL}>{t('products:libraries.options.autoAttach')}</span>
      {cats.state === 'loading' && <p className={HINT}>{t('products:libraries.options.categoriesLoading')}</p>}
      {cats.state === 'error' && (
        <p className="flex flex-wrap items-center gap-2 text-xs text-red-700 dark:text-red-300">
          {t('products:libraries.options.categoriesError')}
          {onRetry && (
            <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 font-semibold text-brand">
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
              {t('products:libraries.retry')}
            </button>
          )}
        </p>
      )}
      {cats.state === 'ready' && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {value.length === 0 && readOnly && <span className="text-[13px] text-t4">{t('products:libraries.options.noAutoAttach')}</span>}
            {value.map(id => {
              const o = byId.get(id);
              if (!o) return null;
              const added = !saved.includes(id);
              return (
                <span
                  key={id}
                  title={o.label}
                  className={`inline-flex h-[26px] items-center gap-1.5 rounded-full bg-surface-3/80 pl-2.5 text-[12.5px] font-medium text-t2 ${readOnly ? 'pr-2.5' : 'pr-1'} ${added ? 'ring-2 ring-amber-500' : ''}`}
                >
                  <CatalogToneDot tone={o.tone} />
                  {o.cat.name}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => onChange?.(value.filter(x => x !== id))}
                      aria-label={t('products:libraries.options.removeCategory', { name: o.label })}
                      className="grid h-5 w-5 place-items-center rounded-full text-t4 hover:bg-surface-4/60 hover:text-t1"
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  )}
                </span>
              );
            })}
            {!readOnly && available.length > 0 && (
              <span className="relative inline-flex">
                <Plus className="pointer-events-none absolute left-2.5 top-1/2 h-[13px] w-[13px] -translate-y-1/2 text-t3" aria-hidden="true" />
                <select
                  value=""
                  aria-label={t('products:libraries.options.addCategoryAria')}
                  onChange={e => { const id = Number(e.target.value); if (id) onChange?.([...value, id]); }}
                  className="h-[26px] w-[7.5rem] cursor-pointer appearance-none truncate rounded-full bg-transparent pl-7 pr-3 text-[12.5px] font-medium text-t2 ring-1 ring-inset ring-line-strong hover:bg-surface-3/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <option value="">{t('products:libraries.options.addCategory')}</option>
                  {available.map(o => <option key={o.cat.id} value={o.cat.id}>{o.label}</option>)}
                </select>
              </span>
            )}
          </div>
          {!readOnly && <p className={HINT}>{t('products:libraries.options.autoAttachHint')}</p>}
          {saved.some(id => !value.includes(id)) && !readOnly && (
            <p className="text-xs text-amber-800 dark:text-amber-300">
              {t('products:libraries.options.categoriesRemoved', {
                names: saved.filter(id => !value.includes(id)).map(id => byId.get(id)?.cat.name).filter(Boolean).join(', '),
              })}
            </p>
          )}
        </>
      )}
    </div>
  );
};

/** Κεντρικός κατάλογος: η κάρτα μόνο για ανάγνωση. */
const ReadOnlyBody: React.FC<{
  group: OptionGroup;
  tree: CategoryOption[];
  cats: Categories;
  categoryIdsOf: (groupId: number, defaults: Record<number, number[]>) => number[];
  typeLabel: (type: SelectionType) => string;
}> = ({ group, tree, cats, categoryIdsOf, typeLabel }) => {
  const { t } = useTranslation(['products', 'common']);
  const ids = cats.state === 'ready' ? categoryIdsOf(group.id, cats.defaults) : [];
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-4 pb-5 pl-[18px] pr-[18px] pt-0.5 md:pl-[46px] xl:grid-cols-[minmax(0,1.1fr)_minmax(300px,1fr)]">
      <RowsReadOnly
        rows={group.choices.map(c => ({ key: String(c.id), name: c.name, price: c.price_modifier, is_default: c.is_default }))}
        labels={{
          nameCol: t('products:libraries.options.choiceCol'),
          priceCol: t('products:libraries.options.extraCol'),
          defaultCol: t('products:libraries.options.defaultCol'),
        }}
        emptyText={t('products:libraries.options.noChoices')}
        defaultBadge={t('products:libraries.options.defaultCol')}
      />
      <div className="flex min-w-0 flex-col gap-4 text-[13.5px]">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <dt className="text-t3">{t('products:libraries.options.picks')}</dt>
          <dd className="font-semibold text-t1">{typeLabel(group.selection_type)}</dd>
          <dt className="text-t3">{t('products:libraries.options.required')}</dt>
          <dd className="font-semibold text-t1">{group.is_required ? t('common:yes') : t('common:no')}</dd>
          <dt className="text-t3">{t('products:libraries.usedIn')}</dt>
          <dd className="font-semibold text-t1"><UsageCount count={group.product_count} /></dd>
        </dl>
        <CategoryAttach value={ids} saved={ids} tree={tree} cats={cats} readOnly />
      </div>
    </div>
  );
};

export default OptionGroupsTab;
